
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  // Sulje valikko kun reitti vaihtuu
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const navItems = [
    { name: 'Etusivu', path: '/' },
    { name: 'Kyber', path: '/content/kyber-perusteet' },
    { name: 'AI-turva', path: '/content/ai-safety' },
    { name: 'Mobiili', path: '/content/mobiiliturva' },
    { name: 'Kriisi', path: '/crisis-protocol' },
    { name: 'Audit', path: '/business-audit' },
    { name: 'Admin', path: '/admin' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="bg-slate-950/80 backdrop-blur-md border-b border-white/10 sticky top-0 z-50 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="w-8 h-8 bg-gradient-to-br from-teal-300 to-purple-400 rounded-lg flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-300 shadow-lg shadow-teal-500/20">
                <span className="text-white font-bold text-xs">LAIL</span>
              </div>
              <span className="text-xl font-bold tracking-tight text-white group-hover:text-teal-300 transition-colors duration-300">
                Lapland <span className="aurora-text">AI Lab</span>
              </span>
            </Link>
          </div>
          
          {/* Desktop Nav */}
          <nav className="hidden xl:flex space-x-1 items-center">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative text-[11px] font-bold uppercase tracking-wider transition-all duration-300 hover:text-teal-300 whitespace-nowrap px-3 py-2 rounded-lg group ${
                  isActive(item.path) ? 'text-teal-300 bg-teal-500/5' : 'text-slate-400'
                }`}
              >
                {item.name}
                <span className={`absolute bottom-1 left-3 right-3 h-0.5 bg-teal-400 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ${isActive(item.path) ? 'scale-x-100' : ''}`}></span>
              </Link>
            ))}

            {user ? (
              <div className="flex items-center gap-4 ml-6 pl-6 border-l border-slate-800">
                <div className="text-right hidden 2xl:block">
                  <div className="text-[10px] font-black text-teal-300 uppercase leading-none mb-1">{user.role}</div>
                  <div className="text-[11px] text-slate-500 font-bold">{user.email.split('@')[0]}</div>
                </div>
                <button 
                  onClick={logout}
                  className="w-10 h-10 rounded-xl bg-slate-900/80 border border-white/10 flex items-center justify-center text-slate-500 hover:text-red-400 hover:border-red-500/30 transition-all duration-300 transform hover:scale-105"
                  title="Kirjaudu ulos"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            ) : (
              <Link to="/login" className="ml-6 px-6 py-2 btn-primary text-[11px] font-black uppercase rounded-xl transition-all transform hover:-translate-y-0.5">
                Login
              </Link>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <div className="xl:hidden">
            <button 
                onClick={() => setIsOpen(!isOpen)} 
                className="text-slate-400 p-2 hover:text-white transition-colors focus:outline-none"
                aria-label="Avaa valikko"
            >
              <div className="w-6 h-6 flex flex-col justify-center space-y-1.5 relative">
                  <span className={`block w-6 h-0.5 bg-current rounded-full transform transition-all duration-300 origin-center ${isOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                  <span className={`block w-6 h-0.5 bg-current rounded-full transition-all duration-300 ${isOpen ? 'opacity-0 scale-x-0' : ''}`}></span>
                  <span className={`block w-6 h-0.5 bg-current rounded-full transform transition-all duration-300 origin-center ${isOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      <div 
        className={`xl:hidden absolute top-20 left-0 w-full bg-slate-950/95 backdrop-blur-2xl border-b border-white/10 overflow-hidden transition-all duration-500 cubic-bezier(0.4, 0, 0.2, 1) ${
            isOpen ? 'max-h-[80vh] opacity-100 shadow-2xl' : 'max-h-0 opacity-0'
        }`}
      >
          <div className="flex flex-col p-6 space-y-2">
              {navItems.map((item, index) => (
                <Link
                  key={item.path}
                  to={item.path}
                  style={{ transitionDelay: `${index * 50}ms` }}
                  className={`text-sm font-bold uppercase tracking-wider px-6 py-4 rounded-2xl transition-all transform ${
                    isOpen ? 'translate-x-0 opacity-100' : '-translate-x-4 opacity-0'
                  } ${
                    isActive(item.path) 
                      ? 'text-teal-300 bg-teal-500/10 border border-teal-400/20' 
                      : 'text-slate-400 hover:bg-slate-900 hover:text-white hover:translate-x-2'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              <div className="border-t border-slate-800 pt-6 mt-4">
                  {user ? (
                      <div className={`transition-all duration-500 delay-300 transform ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                          <div className="flex items-center gap-4 mb-6 p-4 bg-slate-900/50 rounded-2xl border border-white/10">
                              <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-300 font-bold shadow-lg shadow-teal-500/10">
                                  {user.email.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                  <div className="text-[10px] font-black text-teal-300 uppercase leading-none mb-1">{user.role}</div>
                                  <div className="text-sm text-white font-bold">{user.email}</div>
                              </div>
                          </div>
                          <button 
                              onClick={() => logout()}
                              className="w-full flex items-center justify-center gap-2 text-sm font-bold bg-slate-900 hover:bg-red-500/10 text-slate-400 hover:text-red-400 border border-slate-800 hover:border-red-500/30 uppercase tracking-wider py-4 rounded-2xl transition-all"
                          >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                              Kirjaudu ulos
                          </button>
                      </div>
                  ) : (
                      <Link 
                          to="/login" 
                          className={`block text-center btn-primary text-sm font-black uppercase rounded-2xl py-4 transition-all duration-500 delay-300 transform ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
                      >
                          Kirjaudu sisään
                      </Link>
                  )}
              </div>
          </div>
      </div>
    </header>
  );
};

export default Header;
